import React, { useState } from 'react';
import './EmailValidation.css';

interface EmailValidationProps {
  onValidationSuccess: (email: string) => void;
  onValidationError: (error: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  showDisclaimer?: boolean;
  disclaimerText?: string;
  /** Optional JSX for disclaimer (e.g. with Terms/Privacy links); overrides disclaimerText when set */
  disclaimerContent?: React.ReactNode;
}

const EmailValidation: React.FC<EmailValidationProps> = ({
  onValidationSuccess,
  onValidationError,
  isLoading = false,
  disabled = false,
  className = '',
  showDisclaimer = true,
  disclaimerText = 'By clicking Send Verification Email, I agree to the Terms and Services and Privacy Policy. By entering my email address, I agree to receive transactional emails.',
  disclaimerContent
}) => {
  const [email, setEmail] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [showError, setShowError] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setShowError(false);
  };

  const handleSendVerificationEmail = async () => {
    if (!email.trim()) {
      setShowError(true);
      onValidationError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setShowError(true);
      onValidationError('Please enter a valid email address');
      return;
    }

    setIsValidating(true);
    setShowError(false);

    try {
      // Call the parent's success handler with the email
      onValidationSuccess(email);
    } catch (error) {
      setShowError(true);
      onValidationError('Failed to send verification email');
    } finally {
      setIsValidating(false);
    }
  };

  const isButtonDisabled = disabled || isLoading || isValidating || !email.trim() || !validateEmail(email);

  return (
    <div className={`email-validation-container ${className}`}>
      <div className="form-field">
        <label htmlFor="email" className="form-label">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          className="email-input"
          value={email}
          onChange={handleEmailChange}
          placeholder="Enter your email address"
          disabled={disabled}
          autoComplete="email"
        />
        {email && validateEmail(email) && (
          <div className="formatted-email-display">
            Verification email will be sent to: {email}
          </div>
        )}
      </div>

      <button
        className="send-verification-btn"
        onClick={handleSendVerificationEmail}
        disabled={isButtonDisabled}
      >
        {isValidating ? 'Sending...' : 'Send Verification Email'}
      </button>

      {showError && (
        <div className="error-message">
          Please enter a valid email address
        </div>
      )}

      {showDisclaimer && (
        <div className="disclaimer">
          {disclaimerContent ?? disclaimerText}
        </div>
      )}
    </div>
  );
};

export default EmailValidation;
